/**
 * Thin wrapper around the WebGL2 renderer.
 *
 * This is the ONLY module in the project permitted to construct or touch
 * `THREE.WebGLRenderer`. Everything else -- the app, the HUD, subsystems --
 * talks to the surface below. That boundary is what makes a later WebGPU swap a
 * change to one file rather than a rewrite, and it is also where per-frame
 * render statistics are captured for the perf HUD.
 */

import * as THREE from 'three';

export interface RenderStats {
  drawCalls: number;
  triangles: number;
  programs: number;
  geometries: number;
  textures: number;
}

const EMPTY_STATS: RenderStats = {
  drawCalls: 0,
  triangles: 0,
  programs: 0,
  geometries: 0,
  textures: 0,
};

export interface RendererOptions {
  antialias: boolean;
  /** Upper bound on device pixel ratio, so 3x phones do not melt. */
  maxPixelRatio: number;
}

const DEFAULT_OPTIONS: RendererOptions = {
  antialias: true,
  maxPixelRatio: 2,
};

/**
 * Tone mapping exposure. Phase 10.
 *
 * ACES compresses highlights and darkens midtones, so the world needs more
 * light through it than it did through the passthrough it replaces. That extra
 * light is spent HERE rather than in `chunk-gen.ts`'s palette: the palette is
 * the single source of truth for what the world is MADE of, and re-tuning it to
 * cancel a tone curve would put a second, hidden copy of the lighting model in
 * the generator -- the same mistake Phase 3a rejected for the sea. One exposure
 * number and one `SUN_INTENSITY_NOON` are the whole compensation.
 */
const TONE_MAPPING_EXPOSURE = 1.35;

export class Renderer {
  private readonly gl: THREE.WebGLRenderer;
  private readonly maxPixelRatio: number;

  private lastStats: RenderStats = { ...EMPTY_STATS };
  private wireframe = false;
  private materialsDirty = true;

  constructor(canvas: HTMLCanvasElement, options: Partial<RendererOptions> = {}) {
    const opts = { ...DEFAULT_OPTIONS, ...options };
    this.maxPixelRatio = opts.maxPixelRatio;

    this.gl = new THREE.WebGLRenderer({
      canvas,
      antialias: opts.antialias,
      // Deterministic screenshots need a buffer we can read back reliably.
      preserveDrawingBuffer: true,
      powerPreference: 'high-performance',
    });
    // Kept from Phase 0, and kept deliberately. Once Phase 10's sky dome covers
    // every pixel this colour is invisible -- which makes it a free diagnostic:
    // a flat dark-blue frame means the dome failed to draw, rather than being a
    // mystery to bisect.
    this.gl.setClearColor(0x0d1218, 1);

    // ------------------------------------------------------------------
    // Phase 10. The output pipeline.
    // ------------------------------------------------------------------
    //
    // Set explicitly even though this is already r185's default. This one
    // setting decides the pixel value of every committed baseline in `shots/`,
    // and a value that important should not be an inherited library default
    // that a `three` minor bump is free to move. Same argument the polygon
    // offset rule below makes about relying on unstated behaviour.
    this.gl.outputColorSpace = THREE.SRGBColorSpace;

    // A physical sky has a sun in it, and a sun without a tone curve clips to a
    // white disc surrounded by a white sky. ACES is the curve the rest of the
    // industry reaches for and, more usefully here, the one `three`'s own
    // `Sky` shader already ends with -- so the dome and the world agree by
    // construction rather than by two tunings that have to be kept in step.
    this.gl.toneMapping = THREE.ACESFilmicToneMapping;
    this.gl.toneMappingExposure = TONE_MAPPING_EXPOSURE;

    // Inert until Phase 10's slice 10.5 gives a light `castShadow` and gives
    // meshes `castShadow` / `receiveShadow`: with no shadow-casting light in
    // the scene, `WebGLShadowMap.render` returns without doing anything and no
    // material gains `USE_SHADOWMAP`. Configured here anyway, because shadow
    // map type is a statement about the graphics API and this is the only file
    // allowed to make one.
    this.gl.shadowMap.enabled = true;
    this.gl.shadowMap.type = THREE.PCFSoftShadowMap;
  }

  /**
   * Exposure, in stops-ish. A uniform, so changing it costs no recompile --
   * which is what makes it safe to put on a debug slider.
   */
  setToneMappingExposure(value: number): void {
    this.gl.toneMappingExposure = value;
  }

  get toneMappingExposure(): number {
    return this.gl.toneMappingExposure;
  }

  /**
   * Turn the shadow pass on or off.
   *
   * Raises `materialsDirty` as well as flipping the flag, because
   * `USE_SHADOWMAP` is part of the program cache key: a material compiled while
   * shadows were off has no shadow code in it and will not grow any just
   * because the renderer changed its mind.
   */
  setShadowsEnabled(enabled: boolean): void {
    if (this.gl.shadowMap.enabled === enabled) return;
    this.gl.shadowMap.enabled = enabled;
    this.materialsDirty = true;
  }

  get shadowsEnabled(): boolean {
    return this.gl.shadowMap.enabled;
  }

  /** Which backend is live. Reported in the HUD so a WebGPU swap is visible. */
  get backend(): string {
    return 'webgl2';
  }

  /** Underlying canvas, for sizing against its client box. */
  get domElement(): HTMLCanvasElement {
    return this.gl.domElement as HTMLCanvasElement;
  }

  resize(width: number, height: number, devicePixelRatio: number): void {
    this.gl.setPixelRatio(Math.min(devicePixelRatio, this.maxPixelRatio));
    this.gl.setSize(width, height, false);
  }

  /**
   * Toggle wireframe across the scene. Applied lazily on the next render so
   * toggling is free when nothing changed.
   */
  setWireframe(enabled: boolean): void {
    if (this.wireframe === enabled) return;
    this.wireframe = enabled;
    this.materialsDirty = true;
  }

  get wireframeEnabled(): boolean {
    return this.wireframe;
  }

  /**
   * Tell the renderer that new objects entered the scene, so wireframe state is
   * re-applied on the next frame. Subsystems that add meshes at runtime (chunk
   * streaming, from Phase 1 on) must call this.
   */
  invalidateMaterials(): void {
    this.materialsDirty = true;
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    if (this.materialsDirty) {
      applyWireframe(scene, this.wireframe);
      this.materialsDirty = false;
    }

    // `info.render` is reset at the start of each render, so it must be read
    // immediately after -- hence caching it here rather than in `stats()`.
    this.gl.render(scene, camera);

    const info = this.gl.info;
    this.lastStats = {
      drawCalls: info.render.calls,
      triangles: info.render.triangles,
      programs: info.programs?.length ?? 0,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
    };
  }

  /** Statistics from the most recent `render()` call. */
  stats(): RenderStats {
    return this.lastStats;
  }

  dispose(): void {
    this.gl.dispose();
  }
}

/**
 * Where a material's requested `polygonOffset` is remembered while wireframe
 * mode has it switched off. See `applyWireframe`.
 */
const OFFSET_REQUESTED = 'polygonOffsetRequested';

/**
 * Set wireframe mode across a subtree, and with it every render state that only
 * means something in one of the two modes.
 *
 * Exported for `renderer.test.ts`. Constructing a `Renderer` needs a WebGL
 * context and a real canvas, so the only way this rule gets a unit test at all
 * is if the rule itself is reachable without one -- and it needs a test more
 * than most of this file, because the failure it fixes is invisible in the near
 * field and shows up as an unreproducible screenshot rather than as a wrong
 * picture.
 */
export function applyWireframe(root: THREE.Object3D, enabled: boolean): void {
  root.traverse((object) => {
    const mesh = object as Partial<THREE.Mesh>;
    if (mesh.material === undefined) return;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of materials) {
      if ('wireframe' in material) {
        (material as THREE.MeshStandardMaterial).wireframe = enabled;
      }
      setPolygonOffset(material, enabled);
    }
  });
}

/**
 * POLYGON OFFSET IS A FILL-MODE CONCEPT, SO WIREFRAME MODE MUST NOT ASK FOR IT.
 *
 * This is the fix for the instability Phase 5 shipped broken and Phase 6a tracked
 * down. `glPolygonOffset` is gated on `GL_POLYGON_OFFSET_FILL`, and desktop GL's
 * companion `GL_POLYGON_OFFSET_LINE` does not exist in WebGL at all -- so what a
 * line primitive's depth is when the state is enabled anyway is simply not
 * specified. SwiftShader's answer turned out to depend on GPU-process state that
 * survives a page: a wireframe view captured FIRST in a browser process was
 * byte-stable, and the same view captured after any shaded view came back
 * different on every run. That made all eight wireframe canonical views
 * containing a deck permanently unreproducible, while the 28 shaded views and the
 * one wireframe view with nothing in the far distance stayed byte-identical.
 *
 * The offset is what keeps a Phase 5 deck in front of the ground it is coplanar
 * with, so it cannot simply be dropped -- turning it off outright moved every
 * SHADED view too, which is how we know it is doing real work. Instead the
 * requested value is remembered on the material and restored the moment
 * wireframe goes off, so filled rendering is bit-for-bit unchanged and wireframe
 * falls back to the honest answer: coplanar lines resolve by draw order, and a
 * deck is drawn after the terrain it sits on. `DECK_LIFT` still separates them
 * anywhere near the camera; in the far field, where a 5 cm lift is inside one
 * depth quantum, the ground wins and the deck's distant lines are hidden. That
 * is stable, which is the only property a byte-comparison harness needs.
 *
 * It lives here rather than in `chunk-mesh.ts` because it is a statement about
 * the graphics API, and this file is the only module allowed to make one.
 */
function setPolygonOffset(material: THREE.Material, wireframe: boolean): void {
  const requested = material.userData[OFFSET_REQUESTED] as boolean | undefined;
  if (requested === undefined) {
    if (!material.polygonOffset) return;
    material.userData[OFFSET_REQUESTED] = true;
    material.polygonOffset = !wireframe;
    return;
  }
  material.polygonOffset = requested && !wireframe;
}
